<template>
  <bm-text
    :value.sync="enGbFormattedText"
    :read-only-value="localeFormattedText"
    type="number"
    v-bind="$attrs"
    :min="min"
    :max="max"
    @focus="onFocus()"
    @blur="onBlur()"
  >
    <template #append-outer>
      <slot name="append-outer" />
    </template>
    <template #append>
      <slot name="append" />
    </template>
    <template #prepend-inner>
      <slot name="prepend-inner" />
    </template>
    <template #prepend>
      <slot name="prepend" />
    </template>
  </bm-text>
</template>

<script setup lang="ts">
/**
 * A number control that allows the user to enter numbers only into the control.
 *
 * We can't use .number here due to the typing provided by BmText, .number changes the v-model to always return a number
 * and BmText expects a string. This directive seems to designed to work directly on the <input type="number"> control
 *
 * The <input type="number"> control handles formatting to the user's preferences, however the underlying number must always
 * be in decimal format. IE: You always provide a "10.1" or "52.321" formatted number, using "100,000.10" or  "52,321" is invalid as
 * JavaScript uses parseFloat under the hood which doesn't understand coma decimals or groupings, only Decimal Literals
 *
 * As we use a custom read-only component that isn't an input field, we need to provide a "readOnlyValue" formatted by BmNumberQ
 * which uses the user's preferences - hence the use of Intl; this will also manage our decimal place fixing.
 *
 * These behaviours are a little unnatural, but they feel like they don't fight against the DOM, JavaScript, or Vuetify.
 *
 * Use 'enforce-min-max' if you want this control to force the value to be within min/max range. Note. the user will see
 * whatever they have typed although the emitted value will be pinned. The control reverts to the pinned value on blur.
 * Validation rules will apply to the user entry not the emitted value.
 */
import { computed, ref, watch } from "vue";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";

interface BmNumberQProps {
  value?: number | null;
  /* The value to emit when the control is empty */
  defaultValue?: number | null;
  /* Emit values rounded to the specified decimal places. The rounded value will be updated visually on blur */
  maxDecimalPlaces?: number | null;
  /* Whether to display a minimum number of decimal places (e.g. render 2 as 2.00 and 4.5 as 4.50)  */
  minDecimalPlaces?: number;
  integral?: boolean;
  /* Prevent the inbuilt browser stepper from dropping below this value */
  min?: number;
  /* Prevent the inbuilt browser stepper from going above this value. */
  max?: number;
  /* Force the number control to only emit values in the min/max range. Not recommended - instead use validation. */
  enforceMinMax?: boolean;
  readOnlyValue?: number | null;
}

interface BmNumberQEvents {
  (event: "update:value", value: number | null): void;
  (event: "blur"): void;
}

const props = withDefaults(defineProps<BmNumberQProps>(), {
  value: null,
  defaultValue: 0,
  integral: false,
  min: Number.NEGATIVE_INFINITY,
  max: Number.POSITIVE_INFINITY,
  enforceMinMax: false,
  readOnlyValue: null,
});

const emit = defineEmits<BmNumberQEvents>();

const hasFocus = ref(false);
const enGbFormattedText = ref("");

// Because we internally want to keep numbers in the en-GB format, whenever we need to actually
// display text, format it to the user's preferences. Normally the input field can handle this
// but in the cases of read-only where we don't use an input field, this is the correct text
const localeFormattedText = computed<string>(() => {
  if (props.readOnlyValue !== null) {
    return tenantService.formatNumber(
      props.readOnlyValue,
      minDecimalPlacesComputed.value,
      maxDecimalPlacesComputed.value
    );
  } else if (props.value !== null) {
    return tenantService.formatNumber(
      props.value,
      minDecimalPlacesComputed.value,
      maxDecimalPlacesComputed.value
    );
  }

  return "";
});

const minDecimalPlacesComputed = computed<number>(
  () =>
    props.minDecimalPlaces ??
    (props.integral ? 0 : tenantService.settings.measureMinDecimalPlaces)
);

const maxDecimalPlacesComputed = computed<number>(() => {
  // Support null for our maximum number of decimal places so that we ignore rounding
  // particularly useful for duty fields
  if (props.maxDecimalPlaces === null) {
    return 20; // 20 Is the general maximum number of fractional digits https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
  }

  return (
    props.maxDecimalPlaces ??
    (props.integral ? 0 : tenantService.settings.measureMaxDecimalPlaces)
  );
});

const updateInternalEnGbTextFromProp = () => {
  if (props.value != null) {
    // use the number formatter purely for dealing with fractional digits
    // Leaky abstraction alert: bm-text uses v-text, which uses input type=number. This forces a javascript type
    // coercion of the string value into a Number value, which requires a dot for a decimal place in the string.
    // so internally use en-GB format for the value, and let the browser handle rendering the number in the user's
    // preferred format when using an input field. (Refer to top comment for additional context)
    // When we need to display the number manually, use "localeFormattedText" instead
    enGbFormattedText.value = Intl.NumberFormat("en-GB", {
      minimumFractionDigits: minDecimalPlacesComputed.value,
      maximumFractionDigits: maxDecimalPlacesComputed.value,
      useGrouping: false, // we must disable grouping as the underlying html control is expecting numbers only
    }).format(props.value);
  } else {
    enGbFormattedText.value = "";
  }
};

const onFocus = () => {
  hasFocus.value = true;
};

const onBlur = () => {
  hasFocus.value = false;
  updateInternalEnGbTextFromProp();
  emit("blur");
};

const sanitise = (val: number): number => {
  let newVal = +val.toFixed(maxDecimalPlacesComputed.value);
  if (props.enforceMinMax) {
    newVal = Math.max(newVal, props.min);
    newVal = Math.min(newVal, props.max);
  }
  return newVal;
};

/**
 * Watches external changes to the value property and updates visible state if we don't have focus.
 */
watch(
  () => props.value,
  () => {
    // ignore external changes until we lose focus
    if (!hasFocus.value) {
      updateInternalEnGbTextFromProp();
    }
  },
  { immediate: true }
);

watch(
  () => enGbFormattedText.value,
  () => {
    // If we dont have focus then do not emit any changes back to bound property as it wasn't this control that made
    // the change.
    if (!hasFocus.value) {
      return;
    }

    // I'm not sure how we are getting null here i think it is coming from VTextField on clear, but we don't want to
    // deal with it so convert to empty string.
    if (enGbFormattedText.value === null) {
      enGbFormattedText.value = "";
    }

    if (enGbFormattedText.value === "") {
      // empty = default
      emit("update:value", props.defaultValue);
    } else {
      // otherwise emit a cleaned version of our visible input (we will update the displayed value on blur).
      emit("update:value", sanitise(Number(enGbFormattedText.value)));
    }
  }
);
</script>

<style lang="sass" scoped>
// Disable arrows on number input for Firefox (inconsistent `input` and `focus` event order)
// For progress: https://github.com/vuetifyjs/vuetify/issues/14623
:deep(input[type="number"])
  -moz-appearance: textfield
</style>
